import React, { useState, useEffect } from 'react';
import { HiOutlineUpload, HiOutlineEye, HiOutlineTrash, HiOutlineStar, HiStar, HiOutlineX, HiOutlineMail } from 'react-icons/hi';
import axiosInstance from '../../../api/axiosConfig';
import './TemplatesTab.css';

const TemplatesTab = ({ canCreate, canDelete, canEdit }) => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);

    // Upload form state
    const [showUploadForm, setShowUploadForm] = useState(false);
    const [templateName, setTemplateName] = useState('');
    const [templateDescription, setTemplateDescription] = useState('');
    const [isDefault, setIsDefault] = useState(false);
    const [templateFile, setTemplateFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    // Preview modal state
    const [previewTemplate, setPreviewTemplate] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    useEffect(() => {
        fetchTemplates();
    }, []);

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/api/aging-reports/templates');
            if (response.data.success) {
                setTemplates(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching templates:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.name.toLowerCase().endsWith('.eml')) {
                alert('Please select a .eml file (email exported from Outlook)');
                e.target.value = '';
                return;
            }
            if (file.size > 10 * 1024 * 1024) {
                alert('File size must be less than 10MB');
                e.target.value = '';
                return;
            }
            setTemplateFile(file);
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();

        if (!templateName.trim()) {
            alert('Please enter a template name');
            return;
        }
        if (!templateFile) {
            alert('Please select a .eml file');
            return;
        }

        try {
            setUploading(true);
            const formData = new FormData();
            formData.append('file', templateFile);
            formData.append('name', templateName.trim());
            if (templateDescription.trim()) {
                formData.append('description', templateDescription.trim());
            }
            formData.append('is_default', isDefault);

            const response = await axiosInstance.post('/api/aging-reports/templates', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response.data.success) {
                alert('Template uploaded successfully!');
                setTemplateName('');
                setTemplateDescription('');
                setIsDefault(false);
                setTemplateFile(null);
                setShowUploadForm(false);
                fetchTemplates();
            }
        } catch (error) {
            console.error('Error uploading template:', error);
            const detail = error.response?.data?.error?.detail || error.response?.data?.message;
            alert(detail || 'Failed to upload template');
        } finally {
            setUploading(false);
        }
    };

    const handlePreview = async (template) => {
        try {
            setPreviewLoading(true);
            setPreviewTemplate({ ...template, html: null });
            const response = await axiosInstance.get(`/api/aging-reports/templates/${template.id}/preview`);
            if (response.data.success) {
                setPreviewTemplate({ ...template, html: response.data.data.html });
            }
        } catch (error) {
            console.error('Error previewing template:', error);
            const detail = error.response?.data?.error?.detail || error.response?.data?.message;
            alert(detail || 'Failed to preview template');
            setPreviewTemplate(null);
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleSetDefault = async (template) => {
        if (!window.confirm(`Set "${template.name}" as the default template for all emails?`)) {
            return;
        }
        try {
            const response = await axiosInstance.put(`/api/aging-reports/templates/${template.id}/set-default`);
            if (response.data.success) {
                fetchTemplates();
            }
        } catch (error) {
            console.error('Error setting default template:', error);
            alert(error.response?.data?.message || 'Failed to set default template');
        }
    };

    const handleDelete = async (template) => {
        if (!window.confirm(`Delete template "${template.name}"? This cannot be undone.`)) {
            return;
        }
        try {
            const response = await axiosInstance.delete(`/api/aging-reports/templates/${template.id}`);
            if (response.data.success) {
                fetchTemplates();
            }
        } catch (error) {
            console.error('Error deleting template:', error);
            alert(error.response?.data?.message || 'Failed to delete template');
        }
    };

    return (
        <div className="templates-tab">
            <div className="templates-header">
                <div>
                    <h2>Email Templates</h2>
                    <p>Manage the email templates used when sending aging reports</p>
                </div>
                {canCreate && (
                    <button className="btn-add-template" onClick={() => setShowUploadForm(!showUploadForm)}>
                        <HiOutlineUpload size={18} />
                        {showUploadForm ? 'Cancel' : 'Upload Template'}
                    </button>
                )}
            </div>

            {showUploadForm && canCreate && (
                <form className="template-upload-form" onSubmit={handleUpload}>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Template Name *</label>
                            <input
                                type="text"
                                value={templateName}
                                onChange={(e) => setTemplateName(e.target.value)}
                                placeholder="e.g. Statement of Account - Van Sales"
                                required
                                disabled={uploading}
                            />
                        </div>
                        <div className="form-group">
                            <label>Description</label>
                            <input
                                type="text"
                                value={templateDescription}
                                onChange={(e) => setTemplateDescription(e.target.value)}
                                placeholder="Optional notes about this template"
                                disabled={uploading}
                            />
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Template File (.eml) *</label>
                            <input
                                type="file"
                                accept=".eml"
                                onChange={handleFileSelect}
                                required
                                disabled={uploading}
                            />
                            <small>Export the email from Outlook as a .eml file. Embedded images are supported.</small>
                        </div>
                        <div className="form-group checkbox-group">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={isDefault}
                                    onChange={(e) => setIsDefault(e.target.checked)}
                                    disabled={uploading}
                                />
                                Set as default template
                            </label>
                        </div>
                    </div>
                    <button type="submit" className="btn-upload-template" disabled={uploading}>
                        {uploading ? 'Uploading...' : 'Upload Template'}
                    </button>
                </form>
            )}

            {loading ? (
                <div className="templates-loading">Loading templates...</div>
            ) : templates.length === 0 ? (
                <div className="templates-empty">
                    <HiOutlineMail size={40} />
                    <p>No templates uploaded yet.</p>
                    <p>The system will use the built-in default template until one is uploaded.</p>
                </div>
            ) : (
                <div className="templates-grid">
                    {templates.map(template => (
                        <div key={template.id} className={`template-card ${template.is_default ? 'default' : ''}`}>
                            <div className="template-card-header">
                                <h3>{template.name}</h3>
                                {template.is_default && <span className="default-badge">Default</span>}
                            </div>
                            {template.description && (
                                <p className="template-description">{template.description}</p>
                            )}
                            <p className="template-date">
                                Added: {template.created_at ? new Date(template.created_at).toLocaleDateString() : '-'}
                            </p>
                            <div className="template-actions">
                                <button
                                    className="btn-action preview"
                                    onClick={() => handlePreview(template)}
                                    title="Preview template"
                                >
                                    <HiOutlineEye size={16} /> Preview
                                </button>
                                {canEdit && !template.is_default && (
                                    <button
                                        className="btn-action set-default"
                                        onClick={() => handleSetDefault(template)}
                                        title="Set as default"
                                    >
                                        <HiOutlineStar size={16} /> Set Default
                                    </button>
                                )}
                                {canDelete && (
                                    <button
                                        className="btn-action delete"
                                        onClick={() => handleDelete(template)}
                                        title="Delete template"
                                    >
                                        <HiOutlineTrash size={16} /> Delete
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* PREVIEW MODAL */}
            {previewTemplate && (
                <div className="template-preview-overlay" onClick={() => setPreviewTemplate(null)}>
                    <div className="template-preview-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="template-preview-header">
                            <h3>
                                {previewTemplate.name}
                                {previewTemplate.is_default && <HiStar size={16} className="default-star" />}
                            </h3>
                            <button className="btn-close-preview" onClick={() => setPreviewTemplate(null)}>
                                <HiOutlineX size={20} />
                            </button>
                        </div>
                        <div className="template-preview-body">
                            {previewLoading || !previewTemplate.html ? (
                                <div className="preview-loading">Loading preview...</div>
                            ) : (
                                <iframe
                                    title="Template preview"
                                    srcDoc={previewTemplate.html}
                                    sandbox=""
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TemplatesTab;
